<template>
    <div class="tlfs-page">
        <!-- Toolbar -->
        <div class="tlfs-toolbar">
            <div class="tlfs-toolbar__left">
                <h3 class="tlfs-toolbar__title">
                    <i class="el-icon-refresh-right"></i> Lịch cố định Teamlead
                </h3>
                <span class="tlfs-total">{{ paging.total }} lịch</span>
                <span v-if="!showTeamLead" class="tlfs-scope">
                    <i class="el-icon-user-solid"></i> Lịch của tôi
                </span>
            </div>
            <div class="tlfs-toolbar__right">
                <el-button size="small" icon="el-icon-date" @click="goCalendar">Xem trên lịch tuần</el-button>
                <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate">
                    Thêm lịch cố định
                </el-button>
            </div>
        </div>

        <p class="tlfs-lead">
            Lịch cố định được khai báo <b>một lần</b> theo các thứ trong tuần và tự sinh ra công việc cho
            <b>các tuần trong phạm vi áp dụng</b> — chọn số tuần/tháng cụ thể hoặc để lặp không giới hạn.
            Ví dụ: <i>"Họp phòng — T2, T6, 14:00–15:30, từ 07/09/2026 trong 3 tháng"</i>.
        </p>

        <!-- Bộ lọc -->
        <div class="tlfs-filter">
            <el-select
                v-if="showTeamLead"
                v-model="filterUserId"
                filterable
                clearable
                size="small"
                placeholder="Tất cả Teamlead"
                class="tlfs-filter__tl"
                @change="reload"
            >
                <el-option v-for="tl in tlcTeamLeads" :key="tl.id" :label="tl.name" :value="tl.id" />
            </el-select>

            <el-input
                v-model="keyword"
                size="small"
                clearable
                prefix-icon="el-icon-search"
                placeholder="Tìm theo nội dung công việc..."
                class="tlfs-filter__kw"
            />

            <el-select
                v-model="filterWeekdays"
                multiple
                collapse-tags
                clearable
                size="small"
                placeholder="Lọc theo thứ"
                class="tlfs-filter__day"
            >
                <el-option v-for="d in weekdays" :key="d.value" :label="d.label" :value="d.value" />
            </el-select>

            <el-button
                v-if="hasLocalFilter"
                size="small"
                icon="el-icon-close"
                class="tlfs-filter__clear"
                @click="clearLocalFilter"
            >
                Xoá lọc
            </el-button>
            <el-button size="small" icon="el-icon-refresh" @click="fetchList">Tải lại</el-button>
        </div>

        <div v-if="hiddenCount" class="tlfs-hint">
            <i class="el-icon-info"></i>
            Đang ẩn {{ hiddenCount }} lịch không khớp bộ lọc nội dung/thứ trong trang này.
        </div>

        <!-- Bảng -->
        <el-table
            :data="filteredRows"
            v-loading="loading"
            border
            size="small"
            class="tlfs-table"
            empty-text="Chưa có lịch cố định nào"
        >
            <el-table-column v-if="showTeamLead" label="Teamlead" min-width="170">
                <template slot-scope="{ row }">
                    <div class="tlfs-owner">
                        <el-avatar :size="28">{{ initials(row.team_lead_name) }}</el-avatar>
                        <span class="tlfs-owner__name">{{ row.team_lead_name || `#${row.team_lead_id}` }}</span>
                    </div>
                </template>
            </el-table-column>

            <el-table-column label="Nội dung công việc" min-width="200">
                <template slot-scope="{ row }">
                    <div class="tlfs-task">{{ row.title || "—" }}</div>
                </template>
            </el-table-column>

            <el-table-column label="Các thứ áp dụng" min-width="210">
                <template slot-scope="{ row }">
                    <span v-for="wd in weekdayValues(row)" :key="wd" class="tlfs-day">{{ weekdayShort[wd] }}</span>
                    <span v-if="!weekdayValues(row).length" class="tlfs-muted">—</span>
                </template>
            </el-table-column>

            <el-table-column label="Khung giờ" width="140" align="center">
                <template slot-scope="{ row }">
                    <span class="tlfs-time">{{ timeText(row) }}</span>
                </template>
            </el-table-column>

            <el-table-column label="Phạm vi áp dụng" width="190" align="center">
                <template slot-scope="{ row }">
                    <div>{{ row.effective_from ? formatDate(row.effective_from) : "—" }}</div>
                    <div class="tlfs-range">
                        <span v-if="row.effective_to">đến {{ formatDate(row.effective_to) }}</span>
                        <span v-else class="tlfs-muted">không giới hạn</span>
                    </div>
                    <el-tag v-if="isExpired(row)" size="mini" type="info" class="tlfs-lock">Hết hạn</el-tag>
                </template>
            </el-table-column>

            <el-table-column label="Người tạo" min-width="140">
                <template slot-scope="{ row }">
                    <span v-if="row.created_by_name">{{ row.created_by_name }}</span>
                    <span v-else class="tlfs-muted">Tự thiết lập</span>
                    <el-tag v-if="row.is_locked" size="mini" type="info" class="tlfs-lock">
                        <i class="el-icon-lock"></i> Đặt hộ
                    </el-tag>
                </template>
            </el-table-column>

            <el-table-column label="Thao tác" width="150" align="center" fixed="right">
                <template slot-scope="{ row }">
                    <el-button size="mini" plain icon="el-icon-edit" @click="openEdit(row)">Sửa</el-button>
                    <el-button size="mini" plain type="danger" icon="el-icon-delete" @click="confirmDelete(row)" />
                </template>
            </el-table-column>
        </el-table>

        <div class="tlfs-paging">
            <el-pagination
                background
                layout="total, sizes, prev, pager, next"
                :current-page="paging.current_page"
                :page-size="paging.per_page"
                :page-sizes="[20, 50, 100]"
                :total="paging.total"
                @current-change="onPageChange"
                @size-change="onSizeChange"
            />
        </div>

        <fixed-schedule-form-dialog
            :visible.sync="dialogVisible"
            :editing="dialogEditing"
            :initial="dialogInitial"
            :team-leads="tlcTeamLeads"
            :show-team-lead="showTeamLead"
            :submitting="submitting"
            :current-user-id="currentUserId"
            @submit="handleSubmit"
        />
    </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
    TLC_GET_SCHEDULES,
    TLC_GET_FIXED_SCHEDULES,
    TLC_CREATE_FIXED_SCHEDULE,
    TLC_UPDATE_FIXED_SCHEDULE,
    TLC_DELETE_FIXED_SCHEDULE,
} from "@/core/services/store/team-leads-calendar/teamLeadsCalendar.module";
import {
    WEEKDAYS,
    WEEKDAY_SHORT,
    isFixedScheduleExpired,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";
import FixedScheduleFormDialog from "./components/FixedScheduleFormDialog.vue";

export default {
    name: "TeamLeadFixedSchedules",
    components: { FixedScheduleFormDialog },
    data() {
        return {
            rows: [],
            paging: { current_page: 1, per_page: 20, total: 0, last_page: 1 },
            loading: false,

            filterUserId: null,
            keyword: "",
            filterWeekdays: [],

            dialogVisible: false,
            dialogEditing: false,
            dialogInitial: {},
            submitting: false,

            weekdays: WEEKDAYS,
            weekdayShort: WEEKDAY_SHORT,
        };
    },
    computed: {
        ...mapGetters(["currentUser", "tlcTeamLeads", "tlcViewAll"]),
        currentUserId() {
            return this.currentUser && this.currentUser.id;
        },
        /** `view_all` từ API quyết định có quản lý lịch của người khác hay không */
        showTeamLead() {
            return this.tlcViewAll !== false;
        },
        hasLocalFilter() {
            return !!String(this.keyword).trim() || !!this.filterWeekdays.length;
        },
        /**
         * Lọc nội dung / thứ được làm ở FE trên dữ liệu của trang hiện tại vì BE chưa
         * công bố tham số tương ứng (lọc theo Teamlead và phân trang vẫn do BE xử lý).
         */
        filteredRows() {
            const kw = String(this.keyword).trim().toLowerCase();
            const days = this.filterWeekdays;
            return this.rows.filter((r) => {
                if (kw && !`${r.title || ""} ${r.team_lead_name || ""}`.toLowerCase().includes(kw)) return false;
                if (days.length && !this.weekdayValues(r).some((wd) => days.includes(wd))) return false;
                return true;
            });
        },
        hiddenCount() {
            return this.rows.length - this.filteredRows.length;
        },
    },
    mounted() {
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Lịch cố định Teamlead", icon: "far fa-calendar-alt" },
        ]);
        this.loadTeamLeads();
    },
    methods: {
        /**
         * Danh sách Teamlead + cờ `view_all` lấy từ `teamlead/schedules` của tuần hiện tại
         * (BE chưa có endpoint danh sách Teamlead riêng). Chạy xong mới tải danh sách lịch
         * để biết có được gửi `user_id` hay không.
         */
        loadTeamLeads() {
            const from = moment().startOf("isoWeek").format("YYYY-MM-DD");
            const to = moment().endOf("isoWeek").format("YYYY-MM-DD");
            this.$store
                .dispatch(TLC_GET_SCHEDULES, { date_from: from, date_to: to })
                .catch(() => {})
                .then(() => this.fetchList());
        },
        fetchList() {
            this.loading = true;
            const params = { page: this.paging.current_page, per_page: this.paging.per_page };
            if (this.showTeamLead && this.filterUserId) params.user_id = this.filterUserId;
            this.$store
                .dispatch(TLC_GET_FIXED_SCHEDULES, params)
                .then(({ rows, paging }) => {
                    this.rows = rows;
                    this.paging = { ...this.paging, ...paging };
                    this.loading = false;
                })
                .catch((res) => {
                    this.rows = [];
                    this.loading = false;
                    this.$message.error(this.errorText(res, "Không tải được danh sách lịch cố định"));
                });
        },
        reload() {
            this.paging.current_page = 1;
            this.fetchList();
        },
        onPageChange(page) {
            this.paging.current_page = page;
            this.fetchList();
        },
        onSizeChange(size) {
            this.paging.per_page = size;
            this.paging.current_page = 1;
            this.fetchList();
        },
        clearLocalFilter() {
            this.keyword = "";
            this.filterWeekdays = [];
        },

        weekdayValues(row) {
            return (row.slots || []).map((s) => Number(s.weekday)).filter(Boolean);
        },
        timeText(row) {
            const slot = (row.slots || [])[0] || {};
            if (!slot.start_time) return "Cả buổi";
            return slot.end_time ? `${slot.start_time} – ${slot.end_time}` : slot.start_time;
        },
        formatDate(value) {
            return moment(value).format("DD/MM/YYYY");
        },
        isExpired(row) {
            return isFixedScheduleExpired(row);
        },
        initials(name) {
            if (!name) return "?";
            const parts = String(name).trim().split(/\s+/);
            return (parts[parts.length - 1][0] || "").toUpperCase();
        },
        errorText(res, fallback) {
            const data = res && (res.data || res);
            return (data && data.message) || fallback;
        },
        goCalendar() {
            this.$router.push({ name: "team-leads-calendar" }).catch(() => {});
        },

        openCreate() {
            this.dialogEditing = false;
            this.dialogInitial = this.filterUserId ? { team_lead_id: this.filterUserId } : {};
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.dialogEditing = true;
            this.dialogInitial = row;
            this.dialogVisible = true;
        },
        confirmDelete(row) {
            this.$confirm(
                `Xoá lịch cố định "${row.title || ""}"? Các công việc chưa diễn ra sinh ra từ lịch này cũng sẽ bị gỡ.`,
                "Xác nhận xoá",
                { confirmButtonText: "Xoá", cancelButtonText: "Huỷ", type: "warning" }
            )
                .then(() => this.remove(row.id))
                .catch(() => {});
        },
        remove(id) {
            this.$store
                .dispatch(TLC_DELETE_FIXED_SCHEDULE, { id })
                .then(() => {
                    this.$message.success("Đã xoá lịch cố định");
                    // Lùi trang nếu vừa xoá bản ghi cuối cùng của trang
                    if (this.rows.length === 1 && this.paging.current_page > 1) {
                        this.paging.current_page -= 1;
                    }
                    this.fetchList();
                })
                .catch((res) => this.$message.error(this.errorText(res, "Xoá thất bại, vui lòng thử lại")));
        },
        handleSubmit({ editing, id, payload }) {
            this.submitting = true;
            const action = editing ? TLC_UPDATE_FIXED_SCHEDULE : TLC_CREATE_FIXED_SCHEDULE;
            const body = editing ? { id, ...payload } : payload;
            this.$store
                .dispatch(action, body)
                .then(() => {
                    this.$message.success(editing ? "Đã cập nhật lịch cố định" : "Đã tạo lịch cố định");
                    this.dialogVisible = false;
                    this.fetchList();
                })
                .catch((res) => this.$message.error(this.errorText(res, "Lưu thất bại, vui lòng thử lại")))
                .then(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style scoped>
.tlfs-page {
    padding: 4px 2px 24px;
}

/* Toolbar */
.tlfs-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 14px;
    padding: 10px 14px;
    margin-bottom: 12px;
}
.tlfs-toolbar__left,
.tlfs-toolbar__right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.tlfs-toolbar__title {
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    color: #1f2430;
    display: flex;
    align-items: center;
    gap: 7px;
}
.tlfs-toolbar__title i {
    color: #6366f1;
}
.tlfs-total {
    font-size: 12px;
    font-weight: 700;
    color: #4f46e5;
    background: #eef2ff;
    border-radius: 999px;
    padding: 3px 10px;
}
.tlfs-scope {
    font-size: 12px;
    font-weight: 700;
    color: #059669;
    background: #ecfdf5;
    border-radius: 999px;
    padding: 3px 10px;
}

.tlfs-lead {
    font-size: 12.5px;
    color: #6b7387;
    line-height: 1.7;
    background: #f6f7fc;
    border-radius: 12px;
    padding: 10px 14px;
    margin-bottom: 12px;
}

/* Bộ lọc */
.tlfs-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 14px;
    padding: 10px 14px;
    margin-bottom: 12px;
}
.tlfs-filter__tl {
    width: 210px;
}
.tlfs-filter__kw {
    width: 260px;
}
.tlfs-filter__day {
    width: 220px;
}
.tlfs-filter__clear {
    margin-left: 0;
}

.tlfs-hint {
    font-size: 12px;
    color: #b45309;
    background: #fef3c7;
    border-radius: 10px;
    padding: 7px 12px;
    margin-bottom: 10px;
}

/* Bảng */
.tlfs-table {
    border-radius: 12px;
    overflow: hidden;
}
.tlfs-owner {
    display: flex;
    align-items: center;
    gap: 8px;
}
.tlfs-owner__name {
    font-weight: 600;
    color: #1f2430;
}
.tlfs-task {
    font-weight: 600;
    color: #1f2430;
    line-height: 1.5;
}
.tlfs-day {
    display: inline-block;
    min-width: 30px;
    text-align: center;
    font-size: 11.5px;
    font-weight: 700;
    color: #4f46e5;
    background: #eef2ff;
    border-radius: 7px;
    padding: 3px 6px;
    margin: 2px 4px 2px 0;
}
.tlfs-time {
    font-variant-numeric: tabular-nums;
    color: #55607a;
}
.tlfs-range {
    font-size: 11.5px;
    color: #6b7387;
}
.tlfs-muted {
    color: #a3a9b8;
}
.tlfs-lock {
    margin-left: 6px;
}

.tlfs-paging {
    display: flex;
    justify-content: flex-end;
    padding-top: 14px;
}

@media (max-width: 720px) {
    .tlfs-filter__tl,
    .tlfs-filter__kw,
    .tlfs-filter__day {
        width: 100%;
    }
}
</style>
